/**
 * HTML Sanitization Utility
 * 
 * Prevents XSS attacks by sanitizing HTML content before rendering.
 * Uses DOMPurify on the client side. During server-side rendering/build,
 * returns content as-is (content comes from trusted CMS sources).
 * 
 * Best Practices Applied:
 * - Client-side sanitization with DOMPurify (v3.2.4+)
 * - Explicit allow-lists for tags and attributes
 * - Strict configuration with FORBID options
 * - Safe handling for SSR environments
 * 
 * @see https://github.com/cure53/DOMPurify
 * @see https://dompurify.com/
 */

import type DOMPurifyType from 'dompurify';

// Check if we're in a browser environment
const isBrowser = typeof window !== 'undefined';

// DOMPurify instance - loaded dynamically on client only
let DOMPurify: typeof DOMPurifyType | null = null;
let isInitialized = false;

/**
 * Initialize DOMPurify on the client side
 * Uses dynamic import for modern ESM compatibility
 */
async function initDOMPurify(): Promise<typeof DOMPurifyType | null> {
  if (!isBrowser) return null;
  if (isInitialized) return DOMPurify;
  
  try {
    const dompurifyModule = await import('dompurify');
    DOMPurify = dompurifyModule.default;
    isInitialized = true;
    
    // Add security hooks after initialization
    if (DOMPurify) {
      // Force rel="noopener noreferrer" on external links
      DOMPurify.addHook('afterSanitizeAttributes', (node) => {
        if (node.tagName === 'A') {
          // Add security attributes to links with target="_blank"
          if (node.getAttribute('target') === '_blank') {
            node.setAttribute('rel', 'noopener noreferrer');
          }
          // Block dangerous href protocols (security check, not actual usage)
          const href = node.getAttribute('href');
          // eslint-disable-next-line no-script-url
          if (href && (href.startsWith('javascript:') || href.startsWith('data:'))) {
            node.removeAttribute('href');
          }
        }
        // Ensure images have alt attributes for accessibility
        if (node.tagName === 'IMG' && !node.hasAttribute('alt')) {
          node.setAttribute('alt', '');
        }
      });
    }
    
    return DOMPurify;
  } catch (error) {
    console.error('[sanitize-html] Failed to load DOMPurify:', error);
    return null;
  }
}

// Initialize immediately on client
if (isBrowser) {
  initDOMPurify();
}

/**
 * Default allowed HTML tags for rich content
 * Follows principle of least privilege - only allow what's needed
 */
const DEFAULT_ALLOWED_TAGS = [
  // Headings
  'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
  // Text content
  'p', 'br', 'hr', 'span', 'div',
  // Inline text semantics
  'a', 'strong', 'b', 'em', 'i', 'u', 's', 'mark', 'small', 'sub', 'sup',
  // Lists
  'ul', 'ol', 'li',
  // Description lists
  'dl', 'dt', 'dd',
  // Quotes
  'blockquote', 'q', 'cite',
  // Code
  'pre', 'code', 'kbd', 'samp',
  // Images and media
  'img', 'figure', 'figcaption', 'picture', 'source',
  // Tables
  'table', 'thead', 'tbody', 'tfoot', 'tr', 'th', 'td', 'caption', 'colgroup', 'col',
  // Semantic sections
  'article', 'section', 'aside', 'header', 'footer', 'main', 'nav',
  'details', 'summary', 'time', 'address',
];

/**
 * Default allowed HTML attributes
 * Explicitly whitelist safe attributes
 */
const DEFAULT_ALLOWED_ATTRS = [
  // Global attributes
  'id', 'class', 'title', 'lang', 'dir',
  // Link attributes
  'href', 'target', 'rel',
  // Image attributes
  'src', 'srcset', 'sizes', 'alt', 'width', 'height', 'loading', 'decoding',
  // Table attributes
  'colspan', 'rowspan', 'scope', 'headers',
  // Media attributes
  'type', 'media',
  // Accessibility
  'role', 'aria-label', 'aria-labelledby', 'aria-describedby', 'aria-hidden',
  // Time element
  'datetime',
];

/**
 * Explicitly forbidden tags for extra security
 * These will never be allowed even if added to ALLOWED_TAGS
 */
const FORBIDDEN_TAGS = [
  'script', 'style', 'iframe', 'frame', 'frameset',
  'object', 'embed', 'applet', 'form', 'input',
  'button', 'select', 'textarea', 'base', 'meta',
  'link', 'noscript', 'template',
];

const SSR_FORBIDDEN_TAG_PATTERN = new RegExp(
  `<\\/?(?:${FORBIDDEN_TAGS.join('|')})(\\s[^>]*)?\\/?>`,
  'gi'
);
const SSR_EVENT_HANDLER_PATTERN = /\s+on[a-z]+\s*=\s*(?:"[^"]*"|'[^']*'|[^\s>]+)/gi;
const SSR_JAVASCRIPT_HREF_PATTERN = /\s+href\s*=\s*(?:"javascript:[^"]*"|'javascript:[^']*')/gi;

function serverSideSanitize(html: string): string {
  let result = html;
  result = result.replace(SSR_FORBIDDEN_TAG_PATTERN, '');
  result = result.replace(SSR_EVENT_HANDLER_PATTERN, '');
  result = result.replace(SSR_JAVASCRIPT_HREF_PATTERN, '');
  return result;
}

/**
 * Explicitly forbidden attributes for extra security
 * Event handlers and dangerous attributes
 */
const FORBIDDEN_ATTRS = [
  // Event handlers
  'onload', 'onerror', 'onclick', 'onmouseover', 'onmouseout',
  'onmouseenter', 'onmouseleave', 'onfocus', 'onblur', 'onsubmit',
  'onreset', 'onkeydown', 'onkeyup', 'onkeypress', 'onchange',
  'oninput', 'ondblclick', 'oncontextmenu', 'onwheel', 'ondrag',
  'ondragend', 'ondragenter', 'ondragleave', 'ondragover', 'ondragstart',
  'ondrop', 'onscroll', 'oncopy', 'oncut', 'onpaste',
  // Dangerous attributes
  'formaction', 'xlink:href', 'action',
];

/**
 * Sanitize HTML content for safe rendering
 * 
 * @param html - The HTML string to sanitize
 * @returns Sanitized HTML string safe for dangerouslySetInnerHTML
 * 
 * @example
 * ```tsx
 * <div dangerouslySetInnerHTML={{ __html: sanitizeHTML(content) }} />
 * ```
 */
export function sanitizeHTML(html: string | undefined | null): string {
  if (!html) return '';
  
  if (!DOMPurify) return serverSideSanitize(html);
  
  return DOMPurify.sanitize(html, {
    ALLOWED_TAGS: DEFAULT_ALLOWED_TAGS,
    ALLOWED_ATTR: DEFAULT_ALLOWED_ATTRS,
    FORBID_TAGS: FORBIDDEN_TAGS,
    FORBID_ATTR: FORBIDDEN_ATTRS,
    ALLOW_DATA_ATTR: false,
    ADD_ATTR: ['target'], // Allow target for links
    FORCE_BODY: true,
    RETURN_TRUSTED_TYPE: false, // Return string, not TrustedHTML
  });
}

/**
 * Sanitize HTML with custom configuration
 * 
 * @param html - The HTML string to sanitize
 * @param options - Custom configuration options
 * @returns Sanitized HTML string
 */
export function sanitizeHTMLCustom(
  html: string | undefined | null,
  options: {
    allowedTags?: string[];
    allowedAttrs?: string[];
    allowDataAttrs?: boolean;
    forbidTags?: string[];
    forbidAttrs?: string[];
  } = {}
): string {
  if (!html) return '';
  
  if (!DOMPurify) return serverSideSanitize(html);
  
  return DOMPurify.sanitize(html, {
    ALLOWED_TAGS: options.allowedTags ?? DEFAULT_ALLOWED_TAGS,
    ALLOWED_ATTR: options.allowedAttrs ?? DEFAULT_ALLOWED_ATTRS,
    FORBID_TAGS: options.forbidTags ?? FORBIDDEN_TAGS,
    FORBID_ATTR: options.forbidAttrs ?? FORBIDDEN_ATTRS,
    ALLOW_DATA_ATTR: options.allowDataAttrs ?? false,
  });
}

/**
 * Sanitize SVG content for safe rendering
 * 
 * @param svg - The SVG string to sanitize
 * @returns Sanitized SVG string
 * 
 * @example
 * ```tsx
 * <div dangerouslySetInnerHTML={{ __html: sanitizeSVG(svgContent) }} />
 * ```
 */
export function sanitizeSVG(svg: string | undefined | null): string {
  if (!svg) return '';
  
  if (!DOMPurify) return serverSideSanitize(svg);
  
  return DOMPurify.sanitize(svg, {
    USE_PROFILES: { svg: true, svgFilters: true },
    ADD_TAGS: ['use'], // Allow SVG use elements
    FORBID_TAGS: ['script', 'style'],
    FORBID_ATTR: FORBIDDEN_ATTRS,
    IN_PLACE: false,
  });
}

/**
 * Strip all HTML tags, returning only text content
 * 
 * @param html - The HTML string to strip
 * @returns Plain text with all HTML removed
 * 
 * @example
 * ```ts
 * stripHTML('<p>Hello <b>World</b></p>') // "Hello World"
 * ```
 */
export function stripHTML(html: string | undefined | null): string {
  if (!html) return '';
  
  // On server-side (during build), strip tags with regex
  // This is safe because we're only extracting text content
  if (!DOMPurify) {
    return html.replace(/<[^>]*>/g, '').trim();
  }
  
  return DOMPurify.sanitize(html, {
    ALLOWED_TAGS: [],
    ALLOWED_ATTR: [],
  });
}

/**
 * Check if DOMPurify is available and ready
 * Useful for components that need to know if sanitization is active
 */
export function isSanitizationReady(): boolean {
  return DOMPurify !== null && isInitialized;
}

/**
 * Force re-initialization of DOMPurify
 * Useful for testing or after dynamic script loading
 */
export async function reinitializeSanitizer(): Promise<boolean> {
  isInitialized = false;
  DOMPurify = null;
  const result = await initDOMPurify();
  return result !== null;
}
